"use client";

import Image from "next/image";
import Link from "next/link";
import { Typography } from "@/components/ui/typography";
import Carousel, { CarouselSlide } from "@/components/ui/carousel";
import { cn } from "@/lib/utils";
import { StaticImageData } from "next/image";
import Icon from "@/components/ui/Icon";

type RawBestDocumentItem = {
  fields?: {
    serviceName?: string;
    serviceDescriptions?: string;
    mainButton?: string;
    url?: string;
    icon?: {
      fields?: {
        file?: {
           url?: string | StaticImageData;
        };
      };
    };
  };
};

type DocumentsSolutionsCard = {
  title?: string;
  image?: string | StaticImageData;
  description?: string;
  urlName?: string;
  url?: string;
} & React.ComponentProps<"div">;

const DocumentsSolutionsCard = ({
  title,
  image,
  urlName,
  description,
  url = "#",
  className,
}: DocumentsSolutionsCard) => {
  return (
    <div
      className={cn(
        "flex flex-col border border-neutral-200 rounded-3xl lg:border-transparent lg:rounded-xl overflow-hidden bg-white p-5 lg:p-6",
        className
      )}
    >
      <div className="flex-auto min-w-0">
        {!!image && (
          <div className="flex justify-start w-12 lg:w-8 shrink-0">
            <Image
              src={image}
              alt={title ?? ""}
              width={48}
              height={48}
              quality={100}
            />
          </div>
        )}
        {!!title && (
          <h3 className="mt-3 text-lg leading-[130%] font-semibold lg:text-2xl lg:mt-4">
            {title}
          </h3>
        )}
        {!!description && (
          <p className="text-gray-600 text-base leading-[160%] lg:text-sm mt-3 lg:mt-2">
            {description}
          </p>
        )}
        {!!urlName && (
          <div className="mt-auto pt-3 lg:pt-4">
            <Link
              href={url}
              className="text-base leading-[150%] text-blue-500 py-[11] md:p-0 font-semibold hover:underline inline-flex items-center lg:pt-0"
            >
              <span>{urlName}</span>
              <Icon id="icon-arrow-right_24"
                width={24}
                height={24}
                className="shrink-0 w-6 h-6 ml-2"
              />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

type DocumentsSolutionsProps = {
  content?: {
    bestDocTitile?: string;
    bestDocDescription?: string;
    bestDocumentGrid?: RawBestDocumentItem[];
  };
};

export default function DocumentsSolutions({
  content,
}: DocumentsSolutionsProps) {
  const title = content?.bestDocTitile;
  const description = content?.bestDocDescription;
  const grid = content?.bestDocumentGrid ?? [];

  const items = grid.map((item, idx) => {
  const fileUrl = item.fields?.icon?.fields?.file?.url;

  return {
    id: idx,
    title: item.fields?.serviceName,
    description: item.fields?.serviceDescriptions,
    urlName: item.fields?.mainButton,
    url: item.fields?.url ?? "#",
    image:
      typeof fileUrl === "string"
        ? fileUrl.startsWith("http") ? fileUrl : "https:" + fileUrl
        : fileUrl,
  };
});

  return (
    <section className="section bg-white lg:bg-background-secondary overflow-hidden">
      <div className="container mx-auto py-12 lg:py-20">
        <div className="flex flex-col justify-center gap-4 md:w-full md:max-w-[800px] md:mx-auto">
          {!!title && (
            <Typography variant="heading4" className="md:text-center">
              <h2>{title}</h2>
            </Typography>
          )}
          {description && (
            <div className="flex md:justify-center text-gray-600 max-w-[824px] mx-auto">
              <Typography
                variant="subHeadingSm"
                className="md:text-center"
                asChild
              >
                <p>{description}</p>
              </Typography>
            </div>
          )}
        </div>
        <Carousel.Mobile
          options={{
            slides: {
              spacing: 24,
              perView: "auto",
            },
          }}
          showPagination
          className="mt-6 md:!grid md:!grid-cols-2 lg:!grid-cols-3 md:!gap-4 lg:!mt-12 !overflow-visible"
        >
          {items.map(({ id, image, title, description, urlName, url }) => {
            return (
              <CarouselSlide
                key={id}
                className="!min-w-[82%] !w-[82%] !md:min-w-auto !w-auto"
              >
                <DocumentsSolutionsCard
                  title={title}
                  image={image}
                  description={description}
                  urlName={urlName}
                  url={url}
                />
              </CarouselSlide>
            );
          })}
        </Carousel.Mobile>
      </div>
    </section>
  );
}
